// Centralized Design System for Multi-N-Back App
// Based on Refactoring UI principles

// Color Palette
export const colors = {
  // Background & Surface Colors
  bgPrimary: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
  bgCard: 'rgba(30, 41, 59, 0.6)',
  bgCardSolid: 'rgba(30, 41, 59, 0.95)',
  bgInput: 'rgba(15, 23, 42, 0.6)',
  bgHover: 'rgba(30, 41, 59, 0.8)',
  
  // Brand Colors
  primary: '#60a5fa', // blue-400
  primaryLight: '#93c5fd', // blue-300
  primaryDark: '#3b82f6', // blue-500
  
  secondary: '#a78bfa', // purple-400
  secondaryLight: '#c4b5fd', // purple-300
  
  accent: '#34d399', // emerald-400
  warning: '#fbbf24', // amber-400
  warningLight: '#fcd34d', // amber-300
  error: '#f87171', // red-400
  
  // Pro/Premium
  gold: '#fbbf24',
  goldLight: '#fcd34d',
  
  // Text Colors
  textPrimary: '#e2e8f0', // slate-200
  textSecondary: '#cbd5e1', // slate-300
  textTertiary: '#94a3b8', // slate-400
  textMuted: '#64748b', // slate-500
  
  // Border Colors
  border: 'rgba(100, 116, 139, 0.3)',
  borderLight: 'rgba(100, 116, 139, 0.2)',
  borderFocus: 'rgba(96, 165, 250, 0.5)',
  
  // Semantic Colors with opacity support
  success: 'rgba(52, 211, 153, 0.1)',
  successBorder: 'rgba(52, 211, 153, 0.3)',
  successText: '#34d399',
  
  info: 'rgba(96, 165, 250, 0.1)',
  infoBorder: 'rgba(96, 165, 250, 0.3)',
  infoText: '#60a5fa',
  
  warningBg: 'rgba(251, 191, 36, 0.1)',
  warningBorder: 'rgba(251, 191, 36, 0.3)',
  warningText: '#fbbf24',
  
  errorBg: 'rgba(248, 113, 113, 0.1)',
  errorBorder: 'rgba(248, 113, 113, 0.3)',
  errorText: '#f87171',
};

// Typography
export const typography = {
  // Font Families
  fontSans: '"DM Sans", system-ui, -apple-system, sans-serif',
  
  // Font Sizes (in rem, from Design Cheatsheet)
  xs: '0.75rem',    // 12px
  sm: '0.875rem',   // 14px
  base: '1rem',     // 16px
  lg: '1.125rem',   // 18px
  xl: '1.25rem',    // 20px
  '2xl': '1.5rem',  // 24px
  '3xl': '1.875rem',// 30px
  '4xl': '2.25rem', // 36px
  '5xl': '3rem',    // 48px
  
  // Font Weights
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
  
  // Line Heights
  tight: 1.2,
  snug: 1.375,
  normal: 1.5,
  relaxed: 1.625,
};

// Spacing Scale (4px base unit)
export const spacing = {
  0: '0',
  1: '0.25rem',   // 4px
  2: '0.5rem',    // 8px
  3: '0.75rem',   // 12px
  4: '1rem',      // 16px
  5: '1.25rem',   // 20px
  6: '1.5rem',    // 24px
  8: '2rem',      // 32px
  10: '2.5rem',   // 40px
  12: '3rem',     // 48px
  16: '4rem',     // 64px
  20: '5rem',     // 80px
  24: '6rem',     // 96px
};

// Border Radius
export const radius = {
  sm: '6px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  full: '9999px',
};

// Shadows
export const shadows = {
  sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px rgba(0, 0, 0, 0.1)',
  lg: '0 8px 16px rgba(0, 0, 0, 0.15)',
  xl: '0 12px 24px rgba(0, 0, 0, 0.2)',
  '2xl': '0 20px 40px rgba(0, 0, 0, 0.3)',
  inner: 'inset 0 2px 4px rgba(0, 0, 0, 0.1)',
};

// Component Styles (reusable)
export const components = {
  // Card styles
  card: {
    background: colors.bgCard,
    backdropFilter: 'blur(20px)',
    border: `1px solid ${colors.border}`,
    borderRadius: radius.lg,
    padding: spacing[6],
  },
  
  // Button variants
  button: {
    primary: {
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
      color: 'white',
      border: 'none',
      fontWeight: typography.semibold,
      transition: 'all 0.2s ease',
    },
    secondary: {
      background: colors.bgInput,
      color: colors.textSecondary,
      border: `1px solid ${colors.border}`,
      fontWeight: typography.medium,
      transition: 'all 0.2s ease',
    },
    ghost: {
      background: 'transparent',
      color: colors.textSecondary,
      border: 'none',
      fontWeight: typography.medium,
      transition: 'all 0.2s ease',
    },
    success: {
      background: colors.successText,
      color: 'white',
      border: 'none',
      fontWeight: typography.semibold,
      transition: 'all 0.2s ease',
    },
    warning: {
      background: colors.warning,
      color: 'white',
      border: 'none',
      fontWeight: typography.semibold,
      transition: 'all 0.2s ease',
    },
  },
  
  // Input styles
  input: {
    background: colors.bgInput,
    border: `1px solid ${colors.border}`,
    borderRadius: radius.md,
    color: colors.textPrimary,
    padding: `${spacing[3]} ${spacing[4]}`,
    fontSize: typography.base,
  },
  
  // Badge styles
  badge: {
    pro: {
      background: colors.warningBg,
      border: `1px solid ${colors.warningBorder}`,
      color: colors.warning,
      borderRadius: radius.sm,
      padding: `${spacing[1]} ${spacing[3]}`,
      fontSize: typography.xs,
      fontWeight: typography.semibold,
    },
    info: {
      background: colors.info,
      border: `1px solid ${colors.infoBorder}`,
      color: colors.infoText,
      borderRadius: radius.sm,
      padding: `${spacing[1]} ${spacing[3]}`,
      fontSize: typography.xs,
      fontWeight: typography.semibold,
    },
  },
};

// Transition/Animation Helpers
export const transitions = {
  fast: '150ms ease',
  base: '200ms ease',
  slow: '300ms ease',
};

// Helper function for gradient text
export const gradientText = (color1, color2) => ({
  background: `linear-gradient(135deg, ${color1} 0%, ${color2} 100%)`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
});

// Helper for hover states
export const hover = {
  scale: { transform: 'scale(1.02)' },
  lift: { transform: 'translateY(-2px)', boxShadow: shadows.lg },
  brightness: { filter: 'brightness(1.1)' },
};

// Responsive Breakpoints
export const breakpoints = {
  mobile: 768,    // < 768px = mobile
  tablet: 1024,   // 768-1024px = tablet
  desktop: 1024   // > 1024px = desktop
};

// Helper to check if mobile
export const isMobile = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < breakpoints.mobile;
};

// Helper to check if tablet
export const isTablet = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth >= breakpoints.mobile && window.innerWidth < breakpoints.desktop;
};

// Helper to check if desktop
export const isDesktop = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth >= breakpoints.desktop;
};

